import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Container, Form } from 'react-bootstrap';
import {
  emailFieldRequired,
  phoneFieldRequired,
  stringFieldRequired,
} from '@/common/utilities/formFieldSchemas.ts';
import { IMaskInput } from 'react-imask';
import RecaptchaField from '@/common/components/RecaptchaField.tsx';
import { useRecaptchaFormSubmit } from '@/common/hooks/useRecaptchaFormSubmit.ts';

const schema = z.object({
  firstName: stringFieldRequired('First name'),
  lastName: stringFieldRequired('Last name'),
  phone: phoneFieldRequired,
  email: emailFieldRequired,
  company: stringFieldRequired('Company'),
});

type FormData = z.infer<typeof schema>;

const PatientReferralForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { recaptchaRef, onSubmit } = useRecaptchaFormSubmit<FormData>(reset);

  return (
    <Container className="py-5" style={{ maxWidth: 600 }}>
      <h2 className="mb-4">Patient Information</h2>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3" controlId="firstName">
          <Form.Label>First name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Jane"
            isInvalid={!!errors.firstName}
            {...register('firstName')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.firstName?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="lastName">
          <Form.Label>Last name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Smith"
            isInvalid={!!errors.lastName}
            {...register('lastName')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.lastName?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="phone">
          <Form.Label>Phone number</Form.Label>
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <IMaskInput
                {...field}
                mask="(000) 000-0000"
                placeholder="(123) 456-7890"
                className={`form-control${errors.phone ? ' is-invalid' : ''}`}
                onAccept={(value) => field.onChange(value)}
              />
            )}
          />
          <Form.Control.Feedback
            type="invalid"
            className={errors.phone ? 'd-block' : ''}
          >
            {errors.phone?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="name@example.com"
            isInvalid={!!errors.email}
            {...register('email')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.email?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-4" controlId="company">
          <Form.Label>Company</Form.Label>
          <Form.Control
            type="text"
            placeholder="Acme Corporation"
            isInvalid={!!errors.company}
            {...register('company')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.company?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <RecaptchaField ref={recaptchaRef} />
        <Button variant="dark" type="submit">
          Submit
        </Button>
      </Form>
    </Container>
  );
};

export default PatientReferralForm;
